import { groupBy, mapValues, maxBy, take, uniq, uniqWith } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { ProductionComponentInstance, ProductionComponentInstanceStatus, Truss } from '@paragon/contract-library';
import { distanceEquals } from '@paragon/geometry-library';
import { createMappingByKey } from '@paragon/production-app-utilities';
import { groupByWithoutNullishKeys, notNullish } from '@paragon/utilities';

import { AggregatedTrussStatistics } from '../components/pages/gridTypes';
import { RootState } from '../store';
import { getProductionComponentInstancesByBundleGuid } from './getProductionComponentInstancesByBundleGuid';
import { getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTabNotBundledByAnalysisSetGuid } from './getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTabNotBundledByAnalysisSetGuid';
import { getOrderedCheckedAnalysisSetGuidsForUnbundled } from './productionGroupGrid';
import { sumComponentValueForInstances } from './sumComponentValueInBundle';
import { getLoadedTrussesByAnalysisSetGuid, getTrussMeasurements } from './trusses';

export const getBundlesByProductionGroupGuid = createSelector(
  (state: RootState) => state.bundles,
  (state: RootState) => state.productionGroups,

  (bundles, productionGroups) => createMappingByKey(bundles, (bundle) => bundle.productionGroupGuid, productionGroups),
);

export const getBundlesByTruckloadGuid = createSelector(
  (state: RootState) => state.bundles,
  (state: RootState) => state.truckloads,

  (bundles, truckloads) => createMappingByKey(bundles, (bundle) => bundle.truckloadGuid, truckloads),
);

export const getBundlesByScheduleLineGroupGuid = createSelector(
  (state: RootState) => state.scheduleLineGroups,
  (state: RootState) => state.bundles,

  (scheduleLineGroups, bundles) => {
    const bundlesByScheduleLineGroupGuid = groupByWithoutNullishKeys(
      Object.values(bundles),
      (bundle) => bundle.scheduleLineGroupGuid,
    );
    return mapValues(
      scheduleLineGroups,
      (scheduleLineGroup) => bundlesByScheduleLineGroupGuid[scheduleLineGroup.guid] ?? [],
    );
  },
);

export const getAggregatedTrussStatisticsByBundleGuid = createSelector(
  (state: RootState) => state.bundles,
  getProductionComponentInstancesByBundleGuid,
  getTrussMeasurements,

  (
    bundles,
    productionComponentInstancesByBundleGuid,
    trussMeasurements,
  ): { [bundleGuid: string]: AggregatedTrussStatistics } =>
    mapValues(bundles, (bundle) => {
      const productionComponentInstancesForBundle = productionComponentInstancesByBundleGuid[bundle.guid];
      const analysisSetGuids = uniq(productionComponentInstancesForBundle.map((instance) => instance.analysisSetGuid));

      // Do not show values unless all are loaded - we do not want to provide misleading information
      const allTrussesForBundleAreLoaded = analysisSetGuids.every(
        (analysisSetGuid) => analysisSetGuid in trussMeasurements,
      );
      if (!allTrussesForBundleAreLoaded)
        return {
          boardFeet: null,
          linearFeet: null,
          trussCount: sumComponentValueForInstances(productionComponentInstancesForBundle, trussMeasurements, () => 1),
          thicknesses: [],
          widths: [],
          heights: [],
        };

      return {
        boardFeet: sumComponentValueForInstances(
          productionComponentInstancesForBundle,
          trussMeasurements,
          (aggregatedTrussStatistics) => aggregatedTrussStatistics?.boardFeet ?? 0,
        ),
        linearFeet: sumComponentValueForInstances(
          productionComponentInstancesForBundle,
          trussMeasurements,
          (aggregatedTrussStatistics) => aggregatedTrussStatistics?.linearFeet ?? 0,
        ),
        trussCount: sumComponentValueForInstances(productionComponentInstancesForBundle, trussMeasurements, () => 1),
        heights: uniqWith(
          productionComponentInstancesForBundle
            .flatMap((instance) => trussMeasurements[instance.analysisSetGuid]?.heights)
            .filter(notNullish),
          distanceEquals,
        ),
        widths: uniqWith(
          productionComponentInstancesForBundle
            .flatMap((instance) => trussMeasurements[instance.analysisSetGuid]?.widths)
            .filter(notNullish),
          distanceEquals,
        ),
        thicknesses: uniqWith(
          productionComponentInstancesForBundle
            .flatMap((instance) => trussMeasurements[instance.analysisSetGuid]?.thicknesses)
            .filter(notNullish),
          distanceEquals,
        ),
      };
    }),
);

export const getStatusesByBundleGuid = createSelector(
  (state: RootState) => state.bundles,
  getProductionComponentInstancesByBundleGuid,

  (bundles, productionComponentInstancesByBundleGuid) =>
    Object.values(bundles).reduce(
      (statusByBundleGuid, bundle) => {
        const statuses = uniq(productionComponentInstancesByBundleGuid[bundle.guid].map((instance) => instance.status));
        statusByBundleGuid[bundle.guid] = statuses;
        return statusByBundleGuid;
      },
      {} as { [bundleGuid: string]: readonly ProductionComponentInstanceStatus[] },
    ),
);

export const getBundlesForActiveProductionGroupGuid = createSelector(
  getBundlesByProductionGroupGuid,
  (state: RootState) => state.batchesAndBundlesTab.activeProductionGroupGuid,

  (bundlesByProductionGroupGuid, activeProductionGroupGuid) =>
    activeProductionGroupGuid ? bundlesByProductionGroupGuid[activeProductionGroupGuid] : null,
);

export const getTotalThicknessByBundleGuid = createSelector(
  (state: RootState) => state.bundles,
  getProductionComponentInstancesByBundleGuid,
  getLoadedTrussesByAnalysisSetGuid,

  (bundles, productionComponentInstancesByBundleGuid, loadedTrussesByAnalysisSetGuid) =>
    Object.entries(productionComponentInstancesByBundleGuid).reduce(
      (thicknessByBundleGuid, [bundleGuid, productionComponentInstancesForBundle]) => {
        if (productionComponentInstancesForBundle.length === 0) return thicknessByBundleGuid;

        const analysisSetGuidsForBundle = uniq(
          productionComponentInstancesForBundle.map(
            (productionComponentInstance) => productionComponentInstance.analysisSetGuid,
          ),
        );
        // Don't calculate the bundle thickness until all the trusses for the bundle are loaded.
        // This way we can avoid giving an inaccurate but plausible number if some of the trusses fail to load.
        if (analysisSetGuidsForBundle.every((analysisSetGuid) => analysisSetGuid in loadedTrussesByAnalysisSetGuid)) {
          const mostOutlyingTrussInstanceAndYPosition = maxBy(
            productionComponentInstancesForBundle
              .map((instance) =>
                instance.bundlePositionY != null ? { instance, bundlePositionY: instance.bundlePositionY } : null,
              )
              .filter(notNullish),
            ({ bundlePositionY }) => bundlePositionY,
          );
          if (mostOutlyingTrussInstanceAndYPosition == null)
            throw new Error('Could not find most outlying truss in bundle');
          const { instance: mostOutlyingTrussInstance, bundlePositionY: mostOutlyingBundlePositionY } =
            mostOutlyingTrussInstanceAndYPosition;
          const mostOutlyingTruss = loadedTrussesByAnalysisSetGuid[mostOutlyingTrussInstance.analysisSetGuid];
          const mostOutlyingTrussThickness = mostOutlyingTruss.thickness * mostOutlyingTruss.numberOfPlies;
          thicknessByBundleGuid[bundleGuid] = mostOutlyingBundlePositionY + mostOutlyingTrussThickness;
        }

        return thicknessByBundleGuid;
      },

      // Initialize the value for all bundles to zero so that those that do not have production component instances will
      // still have a value in the resulting object.
      mapValues(bundles, () => 0),
    ),
);

export const getInitialAndMaxQuantitiesForBundleCreationOrEditing = createSelector(
  getOrderedCheckedAnalysisSetGuidsForUnbundled,
  getProductionComponentInstancesForActiveProductionGroupBatchesAndBundlesTabNotBundledByAnalysisSetGuid,
  getLoadedTrussesByAnalysisSetGuid,

  (
    orderedCheckedAnalysisSetGuidsForUnbundled,
    productionComponentInstancesNotBundledByAnalysisSetGuid,
    loadedTrussesByAnalysisSetGuid,
  ) => {
    const quantitiesByAnalysisSetGuid: { [analysisSetGuid: string]: number } = {};
    let anyQuantityGreaterThanOne = false;
    for (const analysisSetGuid of orderedCheckedAnalysisSetGuidsForUnbundled) {
      const quantityNotBundled = productionComponentInstancesNotBundledByAnalysisSetGuid[analysisSetGuid].length;
      if (quantityNotBundled === undefined) throw new Error('Quantity not found while creating or adding to a bundle');
      if (quantityNotBundled > 1) anyQuantityGreaterThanOne = true;
      quantitiesByAnalysisSetGuid[analysisSetGuid] = quantityNotBundled;
    }

    return {
      anyQuantityGreaterThanOne,
      initialQuantities: getQuantitiesForBundleWidthLimit(
        orderedCheckedAnalysisSetGuidsForUnbundled,
        quantitiesByAnalysisSetGuid,
        loadedTrussesByAnalysisSetGuid,
      ),
      maxQuantities: quantitiesByAnalysisSetGuid,
    };
  },
);

export const bundleWidthLimit = 48;
function getQuantitiesForBundleWidthLimit(
  orderedCheckedAnalysisSetGuidsForUnbundled: string[],
  quantitiesByAnalysisSetGuid: { [analysisSetGuid: string]: number },
  loadedTrussesByAnalysisSetGuid: { [analysisSetGuid: string]: Truss },
): { [analysisSetGuid: string]: number } {
  const quantitiesForBundleWidthLimitByAnalysisSetGuid: { [analysisSetGuid: string]: number } = {};
  let cumulativeThickness = 0;
  for (const analysisSetGuid of orderedCheckedAnalysisSetGuidsForUnbundled) {
    const { thickness, numberOfPlies } = loadedTrussesByAnalysisSetGuid[analysisSetGuid];
    const componentThickness = thickness * numberOfPlies;
    const fullQuantity = quantitiesByAnalysisSetGuid[analysisSetGuid];
    if (cumulativeThickness + componentThickness * fullQuantity > bundleWidthLimit) {
      const remainingThickness = bundleWidthLimit - cumulativeThickness;
      const partialQuantityThatFits = Math.floor(remainingThickness / componentThickness);
      quantitiesForBundleWidthLimitByAnalysisSetGuid[analysisSetGuid] = partialQuantityThatFits;
      cumulativeThickness += partialQuantityThatFits * componentThickness;
    } else {
      quantitiesForBundleWidthLimitByAnalysisSetGuid[analysisSetGuid] = fullQuantity;
      cumulativeThickness += fullQuantity * componentThickness;
    }
  }
  return quantitiesForBundleWidthLimitByAnalysisSetGuid;
}

/** This function exists to deterministically return the first x production component instance GUIDS for an analysis set
 * GUID. The user is generally setting a quantity for a truss design and is not cognizant or caring of which instances
 * this will include.*/
export function getProductionComponentInstanceGuidsForQuantity(
  productionComponentInstancesNotBundledByAnalysisSetGuid: {
    [analysisSetGuid: string]: readonly ProductionComponentInstance[];
  },
  analysisSetGuid: string,
  quantity: number,
) {
  return take(productionComponentInstancesNotBundledByAnalysisSetGuid[analysisSetGuid], quantity).map(
    (productionComponentInstance) => productionComponentInstance.guid,
  );
}

export const getAnalysisSetGuidQuantitiesByBundle = createSelector(
  getProductionComponentInstancesByBundleGuid,

  (productionComponentInstancesByBundleGuid) =>
    mapValues(productionComponentInstancesByBundleGuid, (instancesForBundle) =>
      mapValues(
        groupBy(instancesForBundle, (instance) => instance.analysisSetGuid),
        (instancesForAnalysisSetGuid) => instancesForAnalysisSetGuid.length,
      ),
    ),
);
